import React from 'react';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { PrRecord } from '@/types/domain/pr-management';
import { Button } from '@/components/ui/Button';
import { Eye, ArrowRight, RefreshCw, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PrTableProps {
  records: PrRecord[];
  isLoading?: boolean;
  onCancelPr?: (id: string) => void;
  onRetrySapSync?: (id: string) => void;
}

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const PrTable: React.FC<PrTableProps> = ({
  records,
  isLoading = false,
  onCancelPr,
  onRetrySapSync,
}) => {
  const navigate = useNavigate();

  const columns: ColumnDef<PrRecord>[] = [
    {
      header: 'PR & Request Trackers',
      accessorKey: 'prNumber',
      cell: (row) => (
        <div className="space-y-1 font-poppins">
          <span
            onClick={() => navigate(`/procurement/pr/${row.id}`)}
            className="font-extrabold text-primary text-xs block hover:underline cursor-pointer"
          >
            {row.prNumber}
          </span>
          <div className="flex flex-wrap gap-1 text-[9.5px] font-mono">
            <span className="bg-muted text-foreground-secondary px-1.5 py-0.5 rounded border border-border">
              {row.requestNumber}
            </span>
            {row.sapDetails?.sapPrNumber && (
              <span className="bg-purple-500/10 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded border border-purple-500/20 font-bold">
                SAP: {row.sapDetails.sapPrNumber}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Requisition Title & Department',
      accessorKey: 'title',
      cell: (row) => (
        <div className="max-w-xs space-y-1 font-poppins">
          <span
            onClick={() => navigate(`/procurement/pr/${row.id}`)}
            className="font-bold text-foreground text-xs block truncate hover:text-primary cursor-pointer"
            title={row.title}
          >
            {row.title}
          </span>
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className="font-semibold text-foreground-secondary">{row.departmentName}</span>
            <span className="text-foreground-muted">•</span>
            <span className="font-mono text-foreground-muted">{row.costCenterCode}</span>
          </div>
        </div>
      ),
    },
    {
      header: 'Plant & Purchasing Org',
      cell: (row) => (
        <div className="space-y-0.5 font-poppins text-xs">
          <span className="font-mono font-bold text-foreground block">{row.plantCode}</span>
          <span className="text-[10px] text-foreground-secondary block">
            {row.purchasingOrg} / {row.purchasingGroup}
          </span>
        </div>
      ),
    },
    {
      header: 'Total Value',
      accessorKey: 'totalAmount',
      cell: (row) => (
        <span className="font-poppins font-bold text-xs text-foreground font-mono">
          {formatInr(row.totalAmount)}
        </span>
      ),
    },
    {
      header: 'Priority',
      accessorKey: 'priority',
      cell: (row) => {
        const isUrgent = row.priority === 'CRITICAL' || row.priority === 'HIGH';
        return (
          <span
            className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full font-poppins ${
              isUrgent
                ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20'
                : 'bg-muted text-foreground-secondary border border-border'
            }`}
          >
            {row.priority}
          </span>
        );
      },
    },
    {
      header: 'Status',
      cell: (row) => {
        const isCreated = row.status === 'SAP_CREATED';
        const isFailed = row.status === 'VALIDATION_FAILED';
        return (
          <div className="space-y-1 font-poppins">
            <span
              className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full inline-block ${
                isCreated
                  ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20'
                  : isFailed
                  ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20'
                  : 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20'
              }`}
            >
              {row.status.replace(/_/g, ' ')}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1.5 font-poppins">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/procurement/pr/${row.id}`)}
            className="h-8 px-2.5 text-xs font-bold rounded-xl border-border text-foreground hover:bg-muted/80 flex items-center gap-1.5 shadow-2xs"
          >
            <Eye className="h-3.5 w-3.5 text-primary" />
            <span>Workspace</span>
            <ArrowRight className="h-3 w-3 text-foreground-muted" />
          </Button>

          {row.status === 'VALIDATION_FAILED' && onRetrySapSync && (
            <Button
              size="sm"
              onClick={() => onRetrySapSync(row.id)}
              className="h-8 px-2.5 text-xs font-bold rounded-xl bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3 mr-0.5" /> Retry
            </Button>
          )}

          {row.status !== 'SAP_CREATED' && row.status !== 'CANCELLED' && onCancelPr && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancelPr(row.id)}
              className="h-8 px-2 text-xs font-bold rounded-xl text-rose-600 border-rose-200 hover:bg-rose-50"
              title="Cancel PR"
            >
              <XCircle className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="font-poppins">
      <DataTable
        data={records}
        columns={columns}
        isLoading={isLoading}
        searchPlaceholder="Search PR number, request reference, title, plant..."
      />
    </div>
  );
};
